import type { ExotelWhatsappTemplateSummary, IntegrationSetting } from '@ferty9/types';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import type { AdminActions } from '../types';

interface EditableSetting {
  key: string;
  name: string;
  provider: string;
  environment: string;
  value_type: string;
  is_secret: boolean;
  is_active: boolean;
  description?: string | null;
  setting_kind?: string;
  channel?: string | null;
  logical_code?: string | null;
  language_code?: string | null;
  approval_status?: string | null;
  variable_schema_json?: unknown;
  metadata_json?: unknown;
  has_value: boolean;
  masked_value?: string | null;
  updated_by?: string | null;
  updated_at?: string | Date | null;
}

export function SystemSettingsTab({
  active,
  settings,
  canUpdate,
  canRotateSecrets,
  actions,
  onTopBarActionsChange,
}: {
  active: boolean;
  settings: IntegrationSetting[];
  canUpdate: boolean;
  canRotateSecrets: boolean;
  actions: AdminActions;
  onTopBarActionsChange?: (actions: ReactNode | null) => void;
}) {
  const rows: EditableSetting[] = settings.map(item => ({
    key: item.key,
    name: item.name || item.key,
    provider: item.provider,
    environment: item.environment,
    value_type: item.value_type,
    is_secret: item.is_secret,
    is_active: item.is_active,
    description: item.description ?? '',
    setting_kind: item.setting_kind ?? 'CONFIG',
    channel: item.channel ?? '',
    logical_code: item.logical_code ?? '',
    language_code: item.language_code ?? '',
    approval_status: item.approval_status ?? '',
    variable_schema_json: item.variable_schema_json ?? null,
    metadata_json: item.metadata_json ?? null,
    has_value: Boolean(item.has_value),
    masked_value: item.masked_value ?? null,
    updated_by: item.updated_by,
    updated_at: item.updated_at,
  }));
  const [editing, setEditing] = useState<EditableSetting | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<EditableSetting | null>(null);
  const [syncing, setSyncing] = useState<EditableSetting | null>(null);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(() => new Set());
  const providerGroups = rows.reduce<Map<string, EditableSetting[]>>((groups, row) => {
    const provider = row.provider || 'general';
    const group = groups.get(provider) || [];
    group.push(row);
    groups.set(provider, group);
    return groups;
  }, new Map());

  const toggleProvider = (provider: string) => {
    setExpandedProviders((current) => {
      const next = new Set(current);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  };

  useEffect(() => {
    if (!onTopBarActionsChange) return undefined;
    if (!active) return undefined;

    onTopBarActionsChange(
      <button className="btn btn-primary compact-btn" disabled={!canUpdate} onClick={() => setCreating(true)}>
        Add Setting
      </button>,
    );

    return () => onTopBarActionsChange(null);
  }, [active, canUpdate, onTopBarActionsChange]);

  return (
    <section id="tab-settings" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <span className="badge" style={{ background: '#ecfdf5', color: '#047857' }}>Encrypted DB Ready</span>
          <button className="btn btn-primary compact-btn" disabled={!canUpdate} onClick={() => setCreating(true)}>
            Add Setting
          </button>
        </div>

        <div style={tableWrapStyle}>
          <table className="enterprise-table" style={settingsTableStyle}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Provider</th>
                <th>Kind</th>
                <th>Channel</th>
                <th>Type</th>
                <th>Encrypted</th>
                <th>Status</th>
                <th>Value</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...providerGroups.entries()].map(([provider, providerRows]) => {
                const expanded = expandedProviders.has(provider);
                return (
                  <ProviderSettingGroup
                    key={provider}
                    provider={provider}
                    rows={providerRows}
                    expanded={expanded}
                    onToggle={() => toggleProvider(provider)}
                    canUpdate={canUpdate}
                    canRotateSecrets={canRotateSecrets}
                    onEdit={setEditing}
                    onSync={setSyncing}
                    onDelete={setDeleting}
                  />
                );
              })}
              {!rows.length && <tr><td colSpan={11} className="empty-cell">No integration settings configured.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <EditSettingModal
          setting={blankSetting()}
          isNew
          canSave={canUpdate}
          canRotateSecrets={canRotateSecrets}
          onClose={() => setCreating(false)}
          onSave={async payload => {
            await actions.updateIntegrationSetting(payload.key || '', payload);
            setCreating(false);
          }}
          notify={actions.notify}
        />
      )}

      {editing && (
        <EditSettingModal
          setting={editing}
          canSave={canUpdate && (!editing.is_secret || canRotateSecrets)}
          canRotateSecrets={canRotateSecrets}
          onClose={() => setEditing(null)}
          onSave={async payload => {
            await actions.updateIntegrationSetting(editing.key, payload);
            setEditing(null);
          }}
          notify={actions.notify}
        />
      )}

      {deleting && (
        <DeleteSettingModal
          setting={deleting}
          onCancel={() => setDeleting(null)}
          onDelete={async () => {
            await actions.deleteIntegrationSetting(deleting.key);
            setDeleting(null);
          }}
        />
      )}

      {syncing && (
        <SyncExotelTemplateModal
          setting={syncing}
          onCancel={() => setSyncing(null)}
          onLoadTemplates={actions.getExotelWhatsappTemplates}
          onSync={async (templateName) => {
            await actions.syncExotelWhatsappTemplate(syncing.key, templateName);
            setSyncing(null);
          }}
          notify={actions.notify}
        />
      )}
    </section>
  );
}

function ProviderSettingGroup({
  provider,
  rows,
  expanded,
  onToggle,
  canUpdate,
  canRotateSecrets,
  onEdit,
  onSync,
  onDelete,
}: {
  provider: string;
  rows: EditableSetting[];
  expanded: boolean;
  onToggle: () => void;
  canUpdate: boolean;
  canRotateSecrets: boolean;
  onEdit: (row: EditableSetting) => void;
  onSync: (row: EditableSetting) => void;
  onDelete: (row: EditableSetting) => void;
}) {
  return (
    <>
      <tr>
        <td colSpan={11} style={{ padding: 0, background: 'var(--surface-muted, #f8fafc)' }}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '14px 16px',
              border: 0,
              background: 'transparent',
              color: 'var(--text)',
              cursor: 'pointer',
              textAlign: 'left',
              font: 'inherit',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1, color: 'var(--primary, #d5006d)' }}>{expanded ? '⌄' : '›'}</span>
              <strong>{providerLabel(provider)}</strong>
            </span>
            <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>{rows.length} {rows.length === 1 ? 'setting' : 'settings'}</span>
          </button>
        </td>
      </tr>
      {expanded && rows.map((row) => {
        const canEdit = canUpdate && (!row.is_secret || canRotateSecrets);
        const canSyncExotel = canUpdate && row.provider === 'exotel_whatsapp' && row.setting_kind === 'TEMPLATE' && row.channel === 'WHATSAPP';
        return (
          <tr key={row.key}>
            <td><strong>{row.name}</strong><small>{row.description || row.environment}</small></td>
            <td><code>{row.key}</code></td>
            <td>{row.provider}</td>
            <td>{row.setting_kind || 'CONFIG'}</td>
            <td>{row.channel || '-'}</td>
            <td>{row.value_type}</td>
            <td>{row.is_secret ? 'Yes' : 'No'}</td>
            <td><span className={`status-chip ${row.is_active ? 'status-good' : 'status-muted'}`}>{row.is_active ? 'Active' : 'Inactive'}</span></td>
            <td>{row.has_value ? row.masked_value || 'Configured' : 'Not stored'}</td>
            <td><small>{row.updated_by || '-'}<br />{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</small></td>
            <td>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="mini-btn pink" disabled={!canEdit} onClick={() => onEdit(row)}>{row.is_secret ? 'Rotate/Edit' : 'Edit'}</button>
                {canSyncExotel && <button className="mini-btn" disabled={!canUpdate} onClick={() => onSync(row)}>Sync</button>}
                <button className="mini-btn" disabled={!canUpdate || !row.has_value} onClick={() => onDelete(row)}>Delete</button>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

function EditSettingModal({
  setting,
  isNew = false,
  canSave,
  canRotateSecrets,
  onClose,
  onSave,
  notify,
}: {
  setting: EditableSetting;
  isNew?: boolean;
  canSave: boolean;
  canRotateSecrets: boolean;
  onClose: () => void;
  onSave: (payload: { key?: string; name?: string; value: string; isSecret: boolean; provider: string; environment?: string; valueType?: string; isActive?: boolean; description?: string; settingKind?: string; channel?: string; logicalCode?: string; languageCode?: string; approvalStatus?: string; variableSchema?: unknown }) => Promise<void>;
  notify: (message: string) => void;
}) {
  const [key, setKey] = useState(setting.key);
  const [name, setName] = useState(setting.name);
  const [provider, setProvider] = useState(setting.provider);
  const [valueType, setValueType] = useState(setting.value_type);
  const [isSecret, setIsSecret] = useState(setting.is_secret);
  const [settingKind, setSettingKind] = useState(setting.setting_kind || 'CONFIG');
  const [channel, setChannel] = useState(setting.channel || '');
  const [logicalCode, setLogicalCode] = useState(setting.logical_code || '');
  const [languageCode, setLanguageCode] = useState(setting.language_code || '');
  const [approvalStatus, setApprovalStatus] = useState(setting.approval_status || '');
  const [variableSchemaRaw, setVariableSchemaRaw] = useState(formatJson(setting.variable_schema_json));
  const [value, setValue] = useState('');
  const [description, setDescription] = useState(setting.description || '');
  const [environment, setEnvironment] = useState(setting.environment || 'current');
  const [isActive, setIsActive] = useState(setting.is_active);
  const [saving, setSaving] = useState(false);
  const canSaveCurrent = canSave && (!isSecret || canRotateSecrets);

  const save = async () => {
    const normalizedKey = key.trim().toUpperCase();
    if (!canSaveCurrent) {
      notify(isSecret ? 'Secret rotation permission is required.' : 'Update permission is required.');
      return;
    }
    if (!/^[A-Z][A-Z0-9_]{1,119}$/.test(normalizedKey)) {
      notify('Key must use uppercase letters, numbers, and underscores.');
      return;
    }
    if (!name.trim()) {
      notify('Name is required.');
      return;
    }
    if (isNew && !value.trim()) {
      notify('Enter a new value before saving.');
      return;
    }
    const variableSchema = parseOptionalJson(variableSchemaRaw);
    if (variableSchemaRaw.trim() && typeof variableSchema === 'undefined') {
      notify('Template variable schema must be valid JSON.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        key: normalizedKey,
        name: name.trim(),
        value,
        isSecret,
        provider,
        environment,
        valueType,
        isActive,
        description: description.trim(),
        settingKind,
        channel,
        logicalCode,
        languageCode,
        approvalStatus,
        variableSchema,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content admin-premium-modal" style={modalContentStyle}>
        <button onClick={onClose} aria-label="Close editor" className="admin-modal-close">&times;</button>
        <div style={{ paddingRight: 28 }}>
          <h3 style={modalTitleStyle}>{isNew ? 'Add Integration Setting' : isSecret ? 'Edit / Rotate Integration Secret' : 'Edit Integration Setting'}</h3>
          <p style={modalDescriptionStyle}>Update one setting at a time. Secret values are never displayed and must be re-entered when rotated.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <label style={labelStyle}>
            Name
            <input value={name} disabled={!canSaveCurrent || saving} onChange={event => setName(event.target.value)} placeholder="WhatsApp OTP Template" style={inputStyle} />
          </label>
          <label style={labelStyle}>
            Key
            <input value={key} disabled={!isNew || !canSaveCurrent || saving} onChange={event => setKey(event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} placeholder="EXOTEL_WA_OTP_TEMPLATE_NAME" style={inputStyle} />
          </label>
          <label style={labelStyle}>
            Provider
            <input value={provider} disabled={!canSaveCurrent || saving} onChange={event => setProvider(event.target.value)} placeholder="exotel_whatsapp" style={inputStyle} />
          </label>
          <label style={labelStyle}>
            Value Type
            <select value={valueType} disabled={!canSaveCurrent || saving} onChange={event => setValueType(event.target.value)} style={inputStyle}>
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
              <option value="url">URL</option>
              <option value="email">Email</option>
            </select>
          </label>
          <label style={labelStyle}>
            Setting Kind
            <select value={settingKind} disabled={!canSaveCurrent || saving} onChange={event => setSettingKind(event.target.value)} style={inputStyle}>
              <option value="CONFIG">Config</option>
              <option value="CONNECTION">Connection</option>
              <option value="TEMPLATE">Template</option>
            </select>
          </label>
        </div>

        {settingKind === 'TEMPLATE' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <label style={labelStyle}>
              Channel
              <select value={channel} disabled={!canSaveCurrent || saving} onChange={event => setChannel(event.target.value)} style={inputStyle}>
                <option value="">Select Channel</option>
                <option value="SMS">SMS</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="EMAIL">Email</option>
              </select>
            </label>
            <label style={labelStyle}>
              Logical Code
              <input value={logicalCode} disabled={!canSaveCurrent || saving} onChange={event => setLogicalCode(event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} placeholder="REMINDER_1" style={inputStyle} />
            </label>
            <label style={labelStyle}>
              Language
              <input value={languageCode} disabled={!canSaveCurrent || saving} onChange={event => setLanguageCode(event.target.value)} placeholder="en" style={inputStyle} />
            </label>
            <label style={labelStyle}>
              Approval Status
              <select value={approvalStatus} disabled={!canSaveCurrent || saving} onChange={event => setApprovalStatus(event.target.value)} style={inputStyle}>
                <option value="">Not Set</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="IN_REVIEW">In Review</option>
              </select>
            </label>
          </div>
        )}

        <div style={metaGridStyle}>
          <Info label="Storage" value={isSecret ? 'Encrypted secret' : 'Plain operational value'} />
          <Info label="Current" value={setting.has_value ? setting.masked_value || 'Configured' : isNew ? 'New setting' : 'Env fallback'} />
        </div>

        <div style={labelStyle}>
          Encrypted Value?
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={radioPillStyle}><input type="radio" checked={isSecret} disabled={!canSave || !canRotateSecrets || saving} onChange={() => setIsSecret(true)} /> Yes</label>
            <label style={radioPillStyle}><input type="radio" checked={!isSecret} disabled={!canSaveCurrent || saving} onChange={() => setIsSecret(false)} /> No</label>
          </div>
        </div>

        <label style={labelStyle}>
          {isNew ? 'Value' : isSecret ? 'Replacement Secret' : 'New Value'}
          <input
            type={isSecret ? 'password' : 'text'}
            value={value}
            disabled={!canSaveCurrent || saving}
            onChange={event => setValue(event.target.value)}
            placeholder={isSecret ? 'Leave blank to keep existing secret' : 'Leave blank to keep current value'}
            style={inputStyle}
          />
        </label>

        {settingKind === 'TEMPLATE' && (
          <label style={labelStyle}>
            Template Variables JSON
            <textarea
              value={variableSchemaRaw}
              disabled={!canSaveCurrent || saving}
              onChange={event => setVariableSchemaRaw(event.target.value)}
              rows={4}
              placeholder='{"variables":["patient_name","feedback_link","center_name"]}'
              style={{ ...inputStyle, height: 'auto', resize: 'vertical', fontFamily: 'monospace', textTransform: 'none' }}
            />
          </label>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <label style={labelStyle}>
            Environment
            <input value={environment} disabled={!canSaveCurrent || saving} onChange={event => setEnvironment(event.target.value)} style={inputStyle} />
          </label>
          <label style={{ ...labelStyle, alignContent: 'end' }}>
            <span>Status</span>
            <button
              type="button"
              className="btn btn-outline"
              disabled={!canSaveCurrent || saving}
              onClick={() => setIsActive(value => !value)}
              style={{ minHeight: 46, justifyContent: 'center' }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </button>
          </label>
        </div>

        <label style={labelStyle}>
          Description
          <textarea
            value={description}
            disabled={!canSaveCurrent || saving}
            onChange={event => setDescription(event.target.value)}
            rows={3}
            placeholder="Optional admin note for audit context"
            style={{ ...inputStyle, height: 'auto', resize: 'vertical' }}
          />
        </label>

        {!canSaveCurrent && (
          <div style={{ padding: 14, borderRadius: 14, background: '#fff7ed', color: '#9a3412', fontWeight: 800 }}>
            {isSecret ? 'Your role needs integration_settings.rotate_secret to replace this secret.' : 'Your role needs integration_settings.update to update this value.'}
          </div>
        )}

        <div style={modalActionsStyle}>
          <button className="btn btn-outline" onClick={onClose} disabled={saving} style={modalButtonStyle}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!canSaveCurrent || saving} style={modalButtonStyle}>
            {saving ? 'Saving...' : isNew ? 'Add Setting' : isSecret ? 'Save / Rotate Secret' : 'Save Setting'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteSettingModal({
  setting,
  onCancel,
  onDelete,
}: {
  setting: EditableSetting;
  onCancel: () => void;
  onDelete: () => Promise<void>;
}) {
  const [secondsLeft, setSecondsLeft] = useState(10);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = window.setTimeout(() => setSecondsLeft(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const confirmDelete = async () => {
    if (secondsLeft > 0 || deleting) return;
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content admin-premium-modal" style={{ ...modalContentStyle, maxWidth: 560 }}>
        <button onClick={onCancel} aria-label="Close delete confirmation" className="admin-modal-close">&times;</button>
        <div style={{ display: 'grid', gap: 14, textAlign: 'center' }}>
          <div style={{ width: 58, height: 58, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontWeight: 900, fontSize: 28 }}>!</div>
          <h3 style={modalTitleStyle}>Delete Integration Setting</h3>
          <p style={modalDescriptionStyle}>
            This removes the stored database value for <strong>{setting.key}</strong>. Runtime may fall back to the matching `.env` value only when the current integration settings source mode allows fallback.
          </p>
          <div style={{ padding: 14, borderRadius: 14, background: '#fef2f2', color: '#991b1b', fontWeight: 800, textAlign: 'left' }}>
            Confirm only if you understand this can change provider connectivity after cache refresh or restart.
          </div>
        </div>
        <div style={modalActionsStyle}>
          <button className="btn btn-outline" onClick={onCancel} disabled={deleting} style={modalButtonStyle}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => void confirmDelete()}
            disabled={secondsLeft > 0 || deleting}
            style={{ ...modalButtonStyle, background: '#ef4444', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' }}
          >
            {deleting ? 'Deleting...' : secondsLeft > 0 ? `Delete (${secondsLeft}s)` : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SyncExotelTemplateModal({
  setting,
  onCancel,
  onLoadTemplates,
  onSync,
  notify,
}: {
  setting: EditableSetting;
  onCancel: () => void;
  onLoadTemplates: () => Promise<ExotelWhatsappTemplateSummary[]>;
  onSync: (templateName: string) => Promise<void>;
  notify: (message: string) => void;
}) {
  const [templates, setTemplates] = useState<ExotelWhatsappTemplateSummary[]>([]);
  const configuredName = configuredExotelTemplateName(setting);
  const [selected, setSelected] = useState(configuredName);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    onLoadTemplates()
      .then((rows) => {
        if (!active) return;
        const currentName = configuredExotelTemplateName(setting);
        const matched = currentName
          ? rows.filter((row) => row.name.toLowerCase() === currentName.toLowerCase())
          : [];
        setTemplates(matched);
        setSelected(matched[0]?.name || '');
      })
      .catch((error) => {
        notify(error instanceof Error ? error.message : 'Unable to load Exotel templates.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [notify, onLoadTemplates, setting.metadata_json]);

  const selectedTemplate = templates.find((template) => template.name === selected);

  const sync = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await onSync(selected);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content admin-premium-modal" style={{ ...modalContentStyle, maxWidth: 680 }}>
        <button onClick={onCancel} aria-label="Close Exotel template sync" className="admin-modal-close">&times;</button>
        <div style={{ paddingRight: 28 }}>
          <h3 style={modalTitleStyle}>Sync WhatsApp Template</h3>
          <p style={modalDescriptionStyle}>
            Sync only the template name stored in this Integration Setting value. The sync updates language, approval status, Exotel components and variable schema.
          </p>
        </div>

        <Info label="Integration Setting" value={setting.key} />
        <Info label="Configured Template" value={configuredName || 'No template value stored'} />

        {loading ? (
          <div style={{ padding: 18, borderRadius: 14, background: 'var(--bg)', fontWeight: 800 }}>Loading Exotel templates...</div>
        ) : (
          <>
            <label style={labelStyle}>
              Exotel Template
              <select value={selected} onChange={event => setSelected(event.target.value)} disabled={saving || !templates.length} style={inputStyle}>
                {templates.map(template => (
                  <option key={`${template.name}:${template.language}`} value={template.name}>
                    {template.name} / {template.language} / {template.status}
                  </option>
                ))}
              </select>
            </label>

            {selectedTemplate && (
              <div style={metaGridStyle}>
                <Info label="Language" value={selectedTemplate.language || '-'} />
                <Info label="Status" value={selectedTemplate.status || '-'} />
                <Info label="Category" value={selectedTemplate.category || '-'} />
                <Info label="Template ID" value={selectedTemplate.id || 'Not returned'} />
              </div>
            )}

            {!templates.length && (
              <div style={{ padding: 14, borderRadius: 14, background: '#fff7ed', color: '#9a3412', fontWeight: 800 }}>
                {configuredName
                  ? `No Exotel template matched "${configuredName}" for the configured WABA.`
                  : 'Store the approved Exotel template name in this setting value before syncing.'}
              </div>
            )}
          </>
        )}

        <div style={modalActionsStyle}>
          <button className="btn btn-outline" onClick={onCancel} disabled={saving} style={modalButtonStyle}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void sync()} disabled={loading || saving || !selected} style={modalButtonStyle}>
            {saving ? 'Syncing...' : 'Sync from Exotel'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: 12, borderRadius: 12, background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ marginTop: 4, color: 'var(--text)', fontWeight: 800, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function formatJson(value: unknown): string {
  if (value === null || typeof value === 'undefined') return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

function parseOptionalJson(value: string): unknown {
  if (!value.trim()) return null;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function exotelTemplateName(metadata: unknown): string {
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) && typeof (metadata as Record<string, unknown>).exotelTemplateName === 'string'
    ? String((metadata as Record<string, unknown>).exotelTemplateName)
    : '';
}

function configuredExotelTemplateName(setting: EditableSetting): string {
  const fromMetadata = exotelTemplateName(setting.metadata_json).trim();
  if (fromMetadata) return fromMetadata;
  if (setting.setting_kind === 'TEMPLATE' && setting.has_value && !setting.is_secret && setting.masked_value) {
    return setting.masked_value.trim();
  }
  return '';
}

function blankSetting(): EditableSetting {
  return {
    key: '',
    name: '',
    provider: 'general',
    environment: 'global',
    value_type: 'string',
    is_secret: false,
    is_active: true,
    description: '',
    setting_kind: 'CONFIG',
    channel: '',
    logical_code: '',
    language_code: '',
    approval_status: '',
    variable_schema_json: null,
    metadata_json: null,
    has_value: false,
    masked_value: null,
    updated_by: null,
    updated_at: null,
  };
}

function providerLabel(provider: string): string {
  const labels: Record<string, string> = {
    looker: 'Looker',
    exotel_sms: 'Exotel SMS',
    exotel_whatsapp: 'Exotel WhatsApp',
    microsoft365: 'Microsoft 365',
    resend: 'Resend',
    dashboard_pdf: 'Dashboard PDF',
    survey_appointments: 'Survey Appointments',
    general: 'General',
  };
  return labels[provider] || provider.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const tableWrapStyle: CSSProperties = {
  overflowX: 'auto',
  border: '1px solid var(--border)',
  borderRadius: 16,
};

const settingsTableStyle: CSSProperties = {
  minWidth: 1320,
};

const modalOverlayStyle: CSSProperties = {
  display: 'flex',
  position: 'fixed',
  inset: 0,
  padding: 16,
  background: 'rgba(15, 23, 42, 0.62)',
  backdropFilter: 'blur(8px)',
  zIndex: 99999,
  alignItems: 'center',
  justifyContent: 'center',
  overflowY: 'auto',
};

const modalContentStyle: CSSProperties = {
  width: 'min(720px, 100%)',
  maxHeight: 'calc(100vh - 32px)',
  overflowY: 'auto',
  background: 'var(--surface-solid)',
  border: '1px solid var(--border)',
  borderRadius: 20,
  padding: 'clamp(18px, 3vw, 28px)',
  boxShadow: 'var(--shadow-lg)',
  position: 'relative',
  display: 'grid',
  gap: 16,
};

const modalTitleStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'Outfit, sans-serif',
  fontSize: 'clamp(20px, 4vw, 26px)',
  fontWeight: 900,
  color: 'var(--text)',
};

const modalDescriptionStyle: CSSProperties = {
  margin: '8px 0 0',
  color: 'var(--text-muted)',
  fontSize: 14,
  lineHeight: 1.6,
};

const metaGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
  gap: 10,
};

const labelStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  fontWeight: 800,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  fontSize: 11,
};

const inputStyle: CSSProperties = {
  minHeight: 46,
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  padding: '10px 14px',
  fontWeight: 650,
  minWidth: 0,
};

const radioPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 42,
  padding: '8px 14px',
  border: '1px solid var(--border)',
  borderRadius: 12,
  background: 'var(--bg)',
  color: 'var(--text)',
  fontSize: 13,
  fontWeight: 800,
  textTransform: 'none',
};

const modalActionsStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};

const modalButtonStyle: CSSProperties = {
  minWidth: 150,
  flex: '1 1 150px',
  justifyContent: 'center',
};
